import * as R from 'ramda';
import memoize from 'memoize-one';
import { withIndex } from '../utils';
import { getDataflow, getDisplay, getTimeDimensionOrders } from './router';
import { getData, getDataflowName } from './sdmx';
import { customAttributes } from '../lib/settings';
import { rules, rules2 } from '@sis-cc/dotstatsuite-components';
import { DISPLAY_BOTH, DISPLAY_CODE, DISPLAY_LABEL } from '../utils/constants';

const getVisState = R.prop('vis');

export const getVisActionId = memoize((state) =>
  R.prop('actionId', getVisState(state)),
);
export const getIsFull = memoize((state) =>
  R.prop('isFull', getVisState(state)),
);
export const getIsOpeningFullscreen = memoize((state) =>
  R.prop('isOpeningFullscreen', getVisState(state)),
);
export const getFilter = memoize((state) =>
  R.prop('filter', getVisState(state)),
);
export const getHasCsvDlStart = memoize((state) =>
  R.prop('hasCsvDlStart', getVisState(state)),
);
export const getHasShared = memoize((state) =>
  R.prop('shared', getVisState(state)),
);

export const getHasDownloadedExcel = memoize((state) =>
  R.pathEq('excel', ['excel', 'id'], getVisState(state)),
);

export const getHasDownloadedPng = memoize((state) =>
  R.pathEq('png', ['png', 'id'], getVisState(state)),
);

export const getVisDimensionFormat = (accessors) =>
  memoize((state) => {
    const display = getDisplay(state);
    const configs = {
      [DISPLAY_LABEL]: rules.dimensionValueDisplay(DISPLAY_LABEL, accessors),
      [DISPLAY_CODE]: rules.dimensionValueDisplay(DISPLAY_CODE, accessors),
      [DISPLAY_BOTH]: rules.dimensionValueDisplay(DISPLAY_BOTH, accessors),
    };
    return R.prop(display, configs);
  });

export const getDataDimensions = memoize((state) => {
  const data = getData(state);
  const dimensions = R.pathOr(
    [],
    ['structure', 'dimensions', 'observation'],
    data,
  );

  return R.addIndex(R.map)((dimension, index) => {
    return R.pipe(
      R.assoc('index', index),
      R.assoc('values', withIndex(R.propOr([], 'values', dimension))),
    )(dimension);
  }, dimensions);
});

export const getVisDataDimensions = memoize((state) => {
  const dimensions = getDataDimensions(state);

  const [many, one] = R.partition(
    (dim) => R.propOr([], 'values', dim).length > 1,
    dimensions,
  );

  return {
    many: R.indexBy(R.prop('id'), many),
    one: R.indexBy(R.prop('id'), one),
  };
});

export const getCustomAttributes = memoize((state) => {
  const data = getData(state);
  const tooltipAttributesIds = rules2.getDataflowTooltipAttributesIds(
    { data },
    customAttributes,
  );
  return R.mergeRight(
    tooltipAttributesIds,
    R.pick(['prefscale', 'decimals', 'rejectedValueIds'], customAttributes),
  );
});

export const getIsTimeInverted = memoize((state) => {
  const orders = getTimeDimensionOrders(state);
  return R.pipe(R.values, R.head, R.equals(true))(orders);
});

export const getAvailableCharts = memoize((state) =>
  rules.getAvailableChartTypes(getData(state)),
);

export const getVisDataflow = memoize((state) => {
  const dataflow = getDataflow(state);
  const name = getDataflowName(state);
  return {
    id: dataflow.dataflowId,
    name,
  };
});
